import 'swiper/css';

import { useEffect, useRef, useState } from 'react';

import { Box, Container, Flex } from '@joinrepublic/dls-ui-kit';
import { useInView } from 'framer-motion';
import type SwiperCore from 'swiper';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { colors, containerSize } from '../../config/styles';
import { FounderSpotlight } from '../founder-spotlight/founder-spotlight';
import { CarouselControl } from './carousel-control';
import { FOUNDER_SPOTLIGHTS } from './data';

const CAROUSEL_AUTOPLAY_DELAY = 10000;
const CAROUSEL_DRAG_SPEED = 250;
const CAROUSEL_AUTO_SPEED = 0;

export const FounderSpotlights = () => {
  const [swiper, setSwiper] = useState<SwiperCore | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });
  const isDragging = useRef(false);

  useEffect(() => {
    if (!swiper) return;
    if (isInView) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }, [isInView, swiper]);

  return (
    <Box
      ref={ref}
      as="section"
      id="founder-spotlights"
      pt={{ base: '4', md: '44px' }}
      pb={{ base: '4', md: '10' }}
      bgColor={colors.darkNavy}
      bgImage={{
        base: 'none',
        sm: 'linear-gradient(to right, transparent 0%, rgba(0 0 0 / 0.45) 100%)',
      }}
      bgSize="50% 100%"
      bgRepeat="no-repeat"
      bgPos="100% 0%"
    >
      <Container maxW={containerSize} px={{ base: '0', sm: '4' }}>
        <Box
          borderRadius={{ base: 'lg', md: 'xl' }}
          overflow={{ base: 'visible', sm: 'hidden' }}
        >
          <Swiper
            modules={[Autoplay]}
            slidesPerView={1}
            spaceBetween={32}
            autoplay={{
              delay: CAROUSEL_AUTOPLAY_DELAY,
              disableOnInteraction: false,
              waitForTransition: true,
              pauseOnMouseEnter: false,
            }}
            speed={CAROUSEL_AUTO_SPEED}
            loop
            onSwiper={(s) => {
              setSwiper(s);
            }}
            onTouchStart={(s) => {
              isDragging.current = true;
              s.params.speed = CAROUSEL_DRAG_SPEED;
              s.autoplay.stop();
            }}
            onTouchEnd={(s) => {
              isDragging.current = false;
              s.autoplay.start();
            }}
            onTransitionEnd={(s) => {
              if (!isDragging.current) {
                s.params.speed = CAROUSEL_AUTO_SPEED;
              }
            }}
            onSlideChange={(s) => {
              setActiveIndex(s.realIndex);
            }}
          >
            {FOUNDER_SPOTLIGHTS.map((founderSpotlight) => (
              <SwiperSlide key={founderSpotlight.company}>
                <FounderSpotlight founderSpotlight={founderSpotlight} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        <Flex
          gap={{ base: '2', sm: '6' }}
          mt={{ base: '12', sm: '10' }}
          px={{ base: '4', sm: '0' }}
        >
          {FOUNDER_SPOTLIGHTS.map((spotlight, index) => (
            <CarouselControl
              key={index}
              company={spotlight.company}
              delay={CAROUSEL_AUTOPLAY_DELAY}
              isActive={isInView && activeIndex === index}
              onClick={() => swiper?.slideToLoop(index, CAROUSEL_AUTO_SPEED)}
            />
          ))}
        </Flex>
      </Container>
    </Box>
  );
};
